// Pieces more than one analytics screen needs.
//
// Nothing here renders a whole view. Each view lives in its own file beside
// this one and imports what it needs.
//
// Every screen that takes a period uses the SAME control strip: a preset
// select, then a from/to pair plus Apply when the preset is "custom". The Vue
// original only refetched a custom range on Apply, and that is preserved here
// by keeping the applied range in separate state from the draft the two date
// inputs write to. An operator half-way through picking a range never fires a
// query.

import { useCallback, useMemo, useState } from "react";

import { ApiError, http, qs } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Banner,
  Dialog,
  DialogFooter,
  Menu,
  SkeletonRows,
  Spinner,
  useToast,
  type BadgeTone,
} from "../ui";

// ============================================================ small helpers

export function errText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** Local calendar date. An ISO string would shift the day across the boundary. */
export function localDate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

export function titleCase(value: string): string {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

export const nf = new Intl.NumberFormat();
export const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

/** 0s / 45s / 3m 20s / 1h 5m, the Vue formatter, unchanged. */
export function formatSeconds(s: number): string {
  if (!s || s <= 0) return "0s";
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
}

export function stamp(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

/** A rate that is genuinely absent reads "—"; a measured zero reads "0.0%". */
export function pctOrDash(v: number | null | undefined): string {
  return v === null || v === undefined || !Number.isFinite(Number(v))
    ? "—"
    : `${(Number(v) * 100).toFixed(1)}%`;
}

export const roundPct = (v: unknown) => `${Math.round((Number(v) || 0) * 100)}%`;

export function num(source: Record<string, unknown> | undefined, key: string): number {
  const value = source?.[key];
  return Number(value) || 0;
}

// ============================================================ not measured
//
// A signal nobody records must never be drawn as 0, or 100%, or an empty
// meter. Those all read as a measurement, and a flattering one. The console
// says the words instead, and says what would have to exist for a number to
// appear here.

/** Stat-tile shape, but the value slot carries the words rather than a figure. */
export function NotMeasuredTile({ label, why }: { label: string; why: string }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value" style={{ fontSize: 17, color: "var(--text-3)" }}>
        Not measured
      </div>
      <div className="delta flat">
        <span className="muted">{why}</span>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ scam types
// Mirrors the classifier's label set. A code with no curated label is
// humanised rather than hidden, so a newly added scam type still reads as
// words on this screen.

export const SCAM_TYPE_LABELS: Record<string, string> = {
  upi: "UPI",
  netbanking: "Net Banking",
  card: "Card",
  card_skimming: "Card Skimming",
  sim_swap: "SIM Swap",
  investment: "Investment",
  lottery_prize: "Lottery / Prize",
  phishing: "Phishing",
  vishing: "Vishing",
  smishing: "Smishing",
  remote_access: "Remote Access",
  digital_arrest: "Digital Arrest",
  romance: "Romance",
  job_task: "Job Task",
  sextortion: "Sextortion",
  aadhaar_aeps: "Aadhaar / AePS",
  kyc_fake_care: "Fake KYC / Customer Care",
  fake_loan_app: "Fake Loan App",
  marketplace: "Marketplace",
  fake_loan_offer: "Fake Loan Offer",
  public_wifi: "Public Wi-Fi",
  fake_ads: "Fake Ads",
  other_fraud: "Other Fraud",
  unclassified: "Unclassified",
};

export const SCAM_TYPE_OPTIONS = Object.entries(SCAM_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

function prettifyCode(code: string): string {
  return code
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function formatScamType(classification: string, subtype: string): string {
  if (!classification) return "";
  if (classification === "other_fraud" && subtype) return prettifyCode(subtype);
  return SCAM_TYPE_LABELS[classification] ?? prettifyCode(classification);
}

// ============================================================ date range

export type RangePreset = "today" | "7days" | "30days" | "this_month" | "custom";
type FixedPreset = Exclude<RangePreset, "custom">;

export interface DateRange {
  from: string;
  to: string;
}

const RANGE_LABEL: Record<RangePreset, string> = {
  today: "Today",
  "7days": "Last 7 days",
  "30days": "Last 30 days",
  this_month: "This month",
  custom: "Custom range",
};

function fixedRange(preset: FixedPreset): DateRange {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === "today") return { from: localDate(today), to: localDate(today) };
  if (preset === "7days") {
    const back = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
    return { from: localDate(back), to: localDate(today) };
  }
  if (preset === "30days") {
    const back = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
    return { from: localDate(back), to: localDate(today) };
  }
  return {
    from: localDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: localDate(today),
  };
}

/**
 * `fallback` is the window a half-filled custom range falls back to, and it is
 * NOT the same on every screen. Chatbot Analytics falls back to 30 days while
 * everything else falls back to the current month. Both are load-bearing: the
 * numbers a screen shows before its range is applied must match what it showed
 * on first load.
 */
function resolveRange(
  preset: RangePreset,
  custom: DateRange,
  fallback: FixedPreset = "this_month"
): DateRange {
  if (preset === "custom") {
    return custom.from && custom.to ? { from: custom.from, to: custom.to } : fixedRange(fallback);
  }
  return fixedRange(preset);
}

interface StoredCalendarRange {
  start?: { year: number; month: number; day: number };
  end?: { year: number; month: number; day: number };
}

/** The Vue console's saved shape, so both builds can share one browser profile. */
function readSavedCustom(key: string): DateRange {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { from: "", to: "" };
    const parsed = JSON.parse(raw) as StoredCalendarRange;
    if (!parsed.start || !parsed.end) return { from: "", to: "" };
    const iso = (p: { year: number; month: number; day: number }) =>
      `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
    return { from: iso(parsed.start), to: iso(parsed.end) };
  } catch {
    return { from: "", to: "" };
  }
}

function writeSavedCustom(key: string, range: DateRange): void {
  const parts = (value: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    return m ? { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) } : null;
  };
  const start = parts(range.from);
  const end = parts(range.to);
  if (!start || !end) return;
  localStorage.setItem(key, JSON.stringify({ start, end }));
}

function readSavedPreset(key: string, fallback: RangePreset): RangePreset {
  const saved = localStorage.getItem(key);
  return saved === "today" ||
    saved === "7days" ||
    saved === "30days" ||
    saved === "this_month" ||
    saved === "custom"
    ? saved
    : fallback;
}

interface RangeControlsProps {
  preset: RangePreset;
  onPreset: (preset: RangePreset) => void;
  draft: DateRange;
  onDraft: (range: DateRange) => void;
  onApply: () => void;
}

/** The shared strip. One row, always above the numbers it filters. */
export function RangeControls({ preset, onPreset, draft, onDraft, onApply }: RangeControlsProps) {
  return (
    <>
      <select
        value={preset}
        aria-label="Date range"
        style={{ width: 170 }}
        onChange={(e) => onPreset(e.target.value as RangePreset)}
      >
        {(Object.keys(RANGE_LABEL) as RangePreset[]).map((key) => (
          <option key={key} value={key}>
            {RANGE_LABEL[key]}
          </option>
        ))}
      </select>
      {preset === "custom" && (
        <>
          <input
            type="date"
            value={draft.from}
            aria-label="Range start"
            onChange={(e) => onDraft({ ...draft, from: e.target.value })}
          />
          <input
            type="date"
            value={draft.to}
            aria-label="Range end"
            onChange={(e) => onDraft({ ...draft, to: e.target.value })}
          />
          <button className="btn" disabled={!draft.from || !draft.to} onClick={onApply}>
            Apply range
          </button>
        </>
      )}
    </>
  );
}

/** Everything a range-driven screen needs, so no view repeats the wiring. */
export function useRange(opts: {
  fallback?: FixedPreset;
  initial?: RangePreset;
  presetKey?: string;
  customKey?: string;
}) {
  const { fallback = "this_month", initial = "this_month", presetKey, customKey } = opts;

  const [preset, setPresetState] = useState<RangePreset>(() =>
    presetKey ? readSavedPreset(presetKey, initial) : initial
  );
  const [applied, setApplied] = useState<DateRange>(() =>
    customKey ? readSavedCustom(customKey) : { from: "", to: "" }
  );
  const [draft, setDraft] = useState<DateRange>(applied);

  const setPreset = useCallback(
    (next: RangePreset) => {
      setPresetState(next);
      if (presetKey) localStorage.setItem(presetKey, next);
    },
    [presetKey]
  );

  const apply = useCallback(() => {
    if (!draft.from || !draft.to) return;
    setApplied(draft);
    if (customKey) writeSavedCustom(customKey, draft);
  }, [draft, customKey]);

  const range = useMemo(() => resolveRange(preset, applied, fallback), [preset, applied, fallback]);

  return { preset, setPreset, draft, setDraft, apply, range };
}

// ============================================================ export menu

type SnapshotType = "chatbot" | "nps" | "billing";

const SNAPSHOT_PATH: Record<SnapshotType, string> = {
  chatbot: "/analytics/chatbot/export",
  nps: "/analytics/nps/export",
  billing: "/billing/usage/export",
};

interface DatasetColumn {
  key: string;
  label: string;
}

interface DatasetConfig {
  table: string;
  label: string;
  description: string;
  columns: DatasetColumn[];
  default_columns: string[];
  supported_filters: { key: string; label: string }[];
  formats: string[];
}

async function saveBlob(res: Response, filename: string): Promise<void> {
  const url = URL.createObjectURL(await res.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

interface ExportMenuProps {
  snapshot: SnapshotType;
  range: DateRange;
  extra?: Record<string, string>;
  /** Enables the raw-row export with its column picker. */
  rawDataset?: string;
}

/**
 * Export is its own permission, not a flavour of write, and the whole control
 * disappears without it. Offering a button that can only 403 is worse than not
 * offering it.
 */
export function ExportMenu({ snapshot, range, extra, rawDataset }: ExportMenuProps) {
  const { me } = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState<"csv" | "json" | null>(null);
  const [config, setConfig] = useState<DatasetConfig | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [configError, setConfigError] = useState<ApiError | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);

  const canExport = Boolean(me?.is_super_admin || me?.permissions?.analytics?.includes("export"));

  const runSnapshot = async (format: "csv" | "json") => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await http.raw(
        `${SNAPSHOT_PATH[snapshot]}${qs({ from: range.from, to: range.to, ...extra, format })}`
      );
      await saveBlob(res, `${snapshot}_export.${format}`);
      toast.push("Export downloaded", "good");
    } catch (err) {
      toast.push(errText(err, "Could not export this view"), "critical");
    } finally {
      setBusy(false);
    }
  };

  const openPicker = async (format: "csv" | "json") => {
    if (!rawDataset) return;
    setPicker(format);
    if (config) return;
    setLoadingConfig(true);
    setConfigError(null);
    try {
      const loaded = await http.get<DatasetConfig>(`/export/${rawDataset}/config`);
      setConfig(loaded);
      setChosen(loaded.default_columns ?? []);
    } catch (err) {
      setConfigError(
        err instanceof ApiError ? err : new ApiError(0, "client_error", String(err), "")
      );
    } finally {
      setLoadingConfig(false);
    }
  };

  const runRaw = async () => {
    if (!rawDataset || !picker) return;
    if (chosen.length === 0) {
      toast.push("Pick at least one column", "warning");
      return;
    }
    setBusy(true);
    try {
      const res = await http.raw("/export", {
        method: "POST",
        body: {
          table: rawDataset,
          columns: chosen,
          filters: { date_from: range.from, date_to: range.to, ...extra },
          format: picker,
        },
      });
      await saveBlob(res, `${rawDataset}_export.${picker}`);
      toast.push("Export downloaded", "good");
      setPicker(null);
    } catch (err) {
      toast.push(errText(err, "Could not export these rows"), "critical");
    } finally {
      setBusy(false);
    }
  };

  if (!canExport) return null;

  return (
    <>
      <Menu
        items={[
          { label: "Export summary CSV", disabled: busy, onSelect: () => void runSnapshot("csv") },
          { label: "Export summary JSON", disabled: busy, onSelect: () => void runSnapshot("json") },
          ...(rawDataset
            ? [
                {
                  sep: true,
                  label: "Export raw CSV",
                  disabled: busy,
                  onSelect: () => void openPicker("csv"),
                },
                { label: "Export raw JSON", disabled: busy, onSelect: () => void openPicker("json") },
              ]
            : []),
        ]}
        trigger={
          <button className="btn" disabled={busy}>
            {busy ? <Spinner label="Exporting" /> : "⭳"} Export
          </button>
        }
      />

      <Dialog open={picker !== null} onClose={() => setPicker(null)} title="Export raw rows">
        <p className="muted">
          {range.from} to {range.to}
          {rawDataset ? ` · ${rawDataset}` : ""}
        </p>
        {configError && (
          <Banner tone="critical" traceId={configError.traceId}>
            Could not load the column list: {configError.message}
          </Banner>
        )}
        {loadingConfig ? (
          <SkeletonRows n={6} cols={1} />
        ) : (
          <div className="col">
            {(config?.columns ?? []).map((col) => (
              <label key={col.key} className="row">
                <input
                  type="checkbox"
                  checked={chosen.includes(col.key)}
                  onChange={(e) =>
                    setChosen((c) =>
                      e.target.checked ? [...c, col.key] : c.filter((k) => k !== col.key)
                    )
                  }
                />
                <span>{col.label}</span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <button className="btn" onClick={() => setPicker(null)}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={busy || chosen.length === 0}
            onClick={() => void runRaw()}
          >
            {busy && <Spinner label="Exporting" />} Download {picker?.toUpperCase()}
          </button>
        </DialogFooter>
      </Dialog>
    </>
  );
}

// ============================================================ tiny presentation

export function KeyCount({
  rows,
  empty,
}: {
  rows: { name: string; count: number }[];
  empty: string;
}) {
  if (rows.length === 0) return <div className="muted">{empty}</div>;
  return (
    <div className="col">
      {rows.map((row) => (
        <div className="row" key={row.name}>
          <span className="truncate">{row.name}</span>
          <span className="spacer" />
          <strong className="num">{nf.format(row.count)}</strong>
        </div>
      ))}
    </div>
  );
}

export function objectRows(value: unknown): { name: string; count: number }[] {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value as Record<string, unknown>).map(([name, count]) => ({
    name,
    count: Number(count) || 0,
  }));
}

// ============================================================ dependencies
// Read by Observability and by Ops recovery, so the row shape and its list
// live here rather than in either one.

interface DependencyRow {
  name: string;
  status: string;
  required: boolean;
  message?: string;
  checked_at: string;
  latency_ms?: number;
  configured: boolean;
}

export interface DependenciesResponse {
  status: string;
  environment?: string;
  trace_id: string;
  dependencies: DependencyRow[];
}

/** ok / degraded+skipped / everything else. Status carries a word as well as a tone. */
function dependencyTone(status: string): BadgeTone {
  if (status === "ok") return "good";
  if (status === "degraded" || status === "skipped") return "warning";
  return "critical";
}

export function DependencyList({ rows }: { rows: DependencyRow[] }) {
  if (rows.length === 0) return <div className="muted">No dependency probes reported.</div>;
  return (
    <div className="col">
      {rows.map((dep) => (
        <div className="row" key={dep.name}>
          <div className="truncate">
            <div className="strong">{dep.name}</div>
            <div className="muted">{dep.message || (dep.required ? "required" : "optional")}</div>
          </div>
          <span className="spacer" />
          {dep.latency_ms !== undefined && <span className="muted num">{dep.latency_ms} ms</span>}
          <Badge tone={dependencyTone(dep.status)}>{dep.status}</Badge>
        </div>
      ))}
    </div>
  );
}
